import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SessionService } from '@monsido/core/session/session.service';
import { AccessibilityRepoService } from '@client/app/services/api/accessibility-repo/accessibility-repo.service';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import {
    AccessibilityChecksRepoService,
} from '@client/app/services/api/accessibility/accessibility-checks-repo/accessibility-checks-repo.service';
import { AccessibilityResultTypeService } from '@client/app/blocks/service/accessibility/result-type.service';
import {
    ChangesHistoryService,
} from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { CrawlHistoryInfoService } from '@client/app/core/services/crawl-history-info/crawl-history-info.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'accessibility-dashboard-guideline',
    standalone: false,
    template: `
<div class="row">
    <div class="col-md-24">
        <accessibility-dashboard-compliance *ngIf="hasLevelACompliance"
                                            lastCrawlTxt="{{ 'Level A failing checks' | translate }}"
                                            header="{{ 'Level A compliance' | translate }}"
                                            theme="fa-mon-wcag-a"
                                            type="A"
                                            [checks]="accessibilityChecks"
                                            [selectedObs]="selectedObservable"
                                            [progress]="progress"
                                            [history]="history">
        </accessibility-dashboard-compliance>

        <accessibility-dashboard-compliance *ngIf="hasLevelAACompliance"
                                            lastCrawlTxt="{{ 'Level AA failing checks' | translate }}"
                                            header="{{ 'Level AA compliance' | translate }}"
                                            theme="fa-mon-wcag-aa"
                                            type="AA"
                                            [checks]="accessibilityChecks"
                                            [selectedObs]="selectedObservable"
                                            [progress]="progress"
                                            [history]="history">
        </accessibility-dashboard-compliance>

        <accessibility-dashboard-compliance *ngIf="hasLevelAAACompliance"
                                            lastCrawlTxt="{{ 'Level AAA failing checks' | translate }}"
                                            header="{{ 'Level AAA compliance' | translate }}"
                                            theme="fa-mon-wcag-aaa"
                                            type="AAA"
                                            [checks]="accessibilityChecks"
                                            [selectedObs]="selectedObservable"
                                            [progress]="progress"
                                            [history]="history">
        </accessibility-dashboard-compliance>

        <div class="card panel-monsido-card">
            <div class="card-body">
                <mon-spinner [progress]="progress">
                    <div class="font-weight-300 font-size-1-3" translate>Accessibility checks compliance by level</div>
                    <accessibility-dashboard-distribution *ngIf="summary"
                                                          [summary]="summary"
                                                          [total]="total">
                    </accessibility-dashboard-distribution>
                </mon-spinner>
            </div>
        </div>
    </div>
    <div class="col-md-24">
        <div class="card panel-monsido-card">
            <div class="card-header">
                <div class="d-flex flex-column flex-sm-row align-items-center">
                    <div class="flex mr-auto">
                        <h2 class="font-weight-300 mb-1" translate>
                            Accessibility Diagnostics
                        </h2>
                        <span translate class="text-grey">
                            Percentage shows the average compliance score across all pages on the domain
                        </span>
                    </div>
                    <div class="d-inline-flex">
                        <mon-action-menu
                            [monActionMenuItems]="dropdownOptions"
                            *ngIf="!accessibilityErrorsOnly"
                            (click)="onToggleAction()">
                            <span *ngIf="selected?.value === null">{{'Showing' | translate}} {{ selected?.label }}</span>
                            <span *ngIf="selected?.value === 'errors'">
                                {{ 'Showing' | translate }}
                                <mon-icon class="mx-1" [monIconDirect]="'faBug'"></mon-icon>
                                {{ 'Only errors' | translate }}
                            </span>
                            <span *ngIf="selected?.value === 'errors_and_warnings'">
                                {{ 'Showing' | translate }}
                                <mon-icon class="mx-1" [monIconDirect]="'faBug'"></mon-icon>
                                {{ 'Errors and' | translate }}
                                <mon-icon class="mx-1" [monIconDirect]="'faBell'"></mon-icon>
                                {{ 'Warnings' | translate }}
                            </span>
                        </mon-action-menu>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <mon-spinner [progress]="progress">
                    <accessibility-dashboard-summary [summary]="summary"
                                                     [accessibilityChecks]="accessibilityChecks"
                                                     [progress]="progress"
                                                     [selectedObs]="selectedObservable"
                                                     [history]="history">
                    </accessibility-dashboard-summary>
                </mon-spinner>
            </div>
            <div class="card-footer bg-white" *ngIf="showHistory">
                <div class="d-flex align-items-center">
                    <div class="mr-auto">&nbsp;</div>
                    <div>
                        <a class="btn-link" uiSref="base.customer.domain.changesHistory" [uiParams]="historyCenterParameters">
                            {{'Show history' | translate}}
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <accessibility-dashboard-pdf *ngIf="pdfscan">
        </accessibility-dashboard-pdf>
    </div>
</div>
    `,
})
export class AccessibilityDashboardComponent implements OnInit, OnDestroy {
    destroyed$ = new Subject<void>();
    accessibilityErrorsOnly = true;
    historyCenterParameters = {};
    summary: any = null;
    history: any = [];
    accessibilityChecks: any = [];
    domain: any;
    pdfscan: any;
    showHistory = false;
    dropdownOptions: any[] = [];
    total = 0;
    selectedObservable = new BehaviorSubject(null);
    progress: any;
    selected: any;

    hasLevelACompliance = false;
    hasLevelAACompliance = false;
    hasLevelAAACompliance = false;

    constructor (
        private ng2SessionService: SessionService,
        private ng2AccessibilityRepoService: AccessibilityRepoService,
        private ng2CrawlsRepoService: CrawlsRepoService,
        private ng2AccessibilityChecksRepoService: AccessibilityChecksRepoService,
        private ng2AccessibilityResultTypeService: AccessibilityResultTypeService,
        private ng2ChangesHistoryService: ChangesHistoryService,
        private ng2ActiveFeatureService: ActiveFeatureService,
        private ng2MonEventsService: MonEventService,
        private ng2CrawlHistoryInfoService: CrawlHistoryInfoService,
        private ng2PipeService: PipeService,
    ) {

    }

    ngOnInit () {
        this.ng2CrawlHistoryInfoService.accessibilityErrorsOnly$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((accessibilityErrorsOnly) => {
                this.accessibilityErrorsOnly = accessibilityErrorsOnly;
            });
        this.domain = this.ng2SessionService.domain;
        this.pdfscan = this.domain.scan.scan_documents;
        this.historyCenterParameters = this.ng2ChangesHistoryService.getHistoryCenterParameters('accessibility');
        this.showHistory = this.ng2ActiveFeatureService.isFeatureActivePure('history_center_all');
        this.dropdownOptions = this.ng2AccessibilityResultTypeService.getResultTypeOptions().map(option => {
            return {
                ...option,
                action: () => {
                    this.selectItem(option);
                    this.ng2MonEventsService.run('actionTrigger',
                        {
                            action: 'accessibility-diagnostic-show-filter-select',
                            params: { selected: option.value },
                        });
                },
            };
        });
        const promises = [this.getHistory(), this.getAccessibilityChecks(), this.getSummary()];
        this.progress = Promise.all(promises).then((values) => {
            this.summary = values[2];
            this.accessibilityChecks = values[1];
            this.history = (values[0] as any).filter((crawl) => {
                return crawl.accessibility_checks && crawl.accessibility_checks.hasOwnProperty('A');
            })
                .reverse();
            this.selectItem(this.dropdownOptions[0]);
            this.getAccessibilityChecksExcludeIgnored();
        }, () => {});
        this.hasLevelComplianceCheck();
    }

    ngOnDestroy () {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    getSummary () {
        return this.ng2AccessibilityRepoService.getSummary();
    }

    onToggleAction () {
        this.ng2MonEventsService.run('actionTrigger', { action: 'accessibility-diagnostic-show-filter' });
    }

    getHistory () {
        const params = {
            page_size: 5,
        };
        return this.ng2CrawlsRepoService.getAll(params);
    }

    getAccessibilityChecks () {
        return this.ng2AccessibilityChecksRepoService.getAll();
    }

    selectItem (item) {
        this.selected = item;
        setTimeout(() => {
            this.selectedObservable.next(this.selected.value);
        });
    }

    getAccessibilityChecksExcludeIgnored () {
        this.total = this.ng2AccessibilityResultTypeService.getAccessibilityChecksExcludeIgnored(this.accessibilityChecks);
    }

    hasLevelComplianceCheck () {
        const hasGuideline = this.ng2PipeService.get('accessibilityHasGuideline');
        const hasGuidelineId = this.ng2PipeService.get('accessibilityHasGuidelineId');
        if (hasGuideline('wcag2')) {
            this.hasLevelACompliance = hasGuidelineId('wcag2-a');
            this.hasLevelAACompliance = hasGuidelineId('wcag2-aa');
            this.hasLevelAAACompliance = hasGuidelineId('wcag2-aaa');
            return;
        }

        if (hasGuideline('wcag21')) {
            this.hasLevelACompliance = hasGuidelineId('wcag21-a') || hasGuidelineId('wcag21-a_mobile');
            this.hasLevelAACompliance = hasGuidelineId('wcag21-aa') || hasGuidelineId('wcag21-aa_mobile');
            this.hasLevelAAACompliance = hasGuidelineId('wcag21-aaa') || hasGuidelineId('wcag21-aaa_mobile');
            return;
        }

        if (hasGuideline('wcag22')) {
            this.hasLevelACompliance = hasGuidelineId('wcag22-a') || hasGuidelineId('wcag22-a_mobile');
            this.hasLevelAACompliance = hasGuidelineId('wcag22-aa') || hasGuidelineId('wcag22-aa_mobile');
            this.hasLevelAAACompliance = hasGuidelineId('wcag22-aaa') || hasGuidelineId('wcag22-aaa_mobile');
            return;
        }

        if (hasGuideline('qualweb')) {
            this.hasLevelACompliance = hasGuidelineId('qualweb-a');
            this.hasLevelAACompliance = hasGuidelineId('qualweb-aa');
            return;
        }
    }
}
